import { useState } from "react"
import type { ComponentProps } from "react"
import Separator from "@/v2-ui/separator"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import { TooltipLink } from "@/v2-backoffice-client/components/link"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import LicensePlanUpdateDialog from "@/v2-backoffice-client/license/plan/update/LicensePlanUpdateDialog"
import LicensePlanDeprecateDialog from "@/v2-backoffice-client/license/plan/deprecate/LicensePlanDeprecateDialog"
import { FENERUM_PLAN_BASE_URL } from "@/v2-backoffice-client/constants"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

export type LicensePlanViewToolbarProps = ComponentProps<"div"> & {
  plan: LicensePlanFragment | null | undefined
}

// The license is Fenerum's: the deep link goes to the system that owns it.
// The two edits offered are the app-owned surfaces — Update License (product
// and seat semantics) and the deprecation (both the same dialogs as the
// board's row menu). Deprecate stands apart, in red, like the row menu's
// item: it marks the license as being retired everywhere it shows.
function LicensePlanViewToolbar(props: LicensePlanViewToolbarProps) {
  const { plan, ...viewSheetToolbarProps } = props
  const [isUpdateOpen, setIsUpdateOpen] = useState(false)
  const [isDeprecateOpen, setIsDeprecateOpen] = useState(false)
  if (!plan) return null

  return (
    <ViewSheet.Toolbar {...viewSheetToolbarProps}>
      <TooltipButton
        variant="ghost"
        isStacked
        iconSrc="edit"
        content="Update the license — the product it is for and how its quantities count seats"
        onClick={() => {
          setIsUpdateOpen(true)
        }}
      >
        Update
      </TooltipButton>

      <Separator orientation="vertical" />

      <TooltipButton
        variant={plan.DeprecatedAt ? "ghost" : "destructive"}
        isStacked
        iconSrc="archive"
        content={
          plan.DeprecatedAt
            ? "Update the deprecation — successor, note, or reactivate the license"
            : "Mark this license as being retired and open a migration worklist for the customers on it"
        }
        onClick={() => {
          setIsDeprecateOpen(true)
        }}
      >
        {plan.DeprecatedAt ? "Deprecation" : "Deprecate"}
      </TooltipButton>

      <Separator orientation="vertical" />

      <TooltipLink
        variant="ghost"
        isStacked
        iconSrc="bank"
        content="Open this license in Fenerum"
        to={`${FENERUM_PLAN_BASE_URL}/${plan.ExternalPlanUuid}/`}
      >
        Fenerum
      </TooltipLink>

      {/* Both dialogs mount per open so their fields re-seed from the plan
          every time: the panel moves between licenses (a seat line's base
          links, the breadcrumb) and a save changes what the fields hold. */}
      {isUpdateOpen && (
        <LicensePlanUpdateDialog
          plan={plan}
          open={isUpdateOpen}
          onOpenChange={setIsUpdateOpen}
        />
      )}

      {isDeprecateOpen && (
        <LicensePlanDeprecateDialog
          plan={plan}
          open={isDeprecateOpen}
          onOpenChange={setIsDeprecateOpen}
        />
      )}
    </ViewSheet.Toolbar>
  )
}

export default LicensePlanViewToolbar
